import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { DealFormModal } from '@/components/deals/DealFormModal';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/use-auth';
import type { Deal } from '@/lib/types';

/**
 * A DEAL NAME ON A LIST SURFACE — a real link that OPENS THE EDIT FORM.
 *
 * The deal half of the pattern `QuoteNumberLink` and `InvoiceNumberLink`
 * established; read `src/components/invoices/InvoiceNumberLink.tsx` for the
 * reasoning, which is copied here rather than re-derived:
 *
 *  - it is still a real `<Link to="/deals/{id}">`, and the click handler RETURNS
 *    EARLY (never `preventDefault`s) on a modified click, a non-primary button,
 *    an event already claimed, or no opener — so ctrl/cmd/middle-click still
 *    open a tab;
 *  - the state and the one form live in {@link DealQuickEdit}, mounted ONCE per
 *    surface, reached through context; a link with no host navigates;
 *  - a click is a FETCH before it is a modal, so the link has a busy mark and
 *    the host has a failure notice that NAMES the deal.
 *
 * ⚠️ **No Xero report.** A deal is never pushed to Xero, and `DealFormModal`
 * takes no `onXeroPush`, so there is nothing to say about a push here.
 */

interface DealQuickEditContextValue {
  /** `null` = navigate instead: no host, or this reader may not update deals. */
  open: ((dealId: number, dealName: string) => void) | null;
  pendingId: number | null;
}

const DealQuickEditContext = createContext<DealQuickEditContextValue>({
  open: null,
  pendingId: null,
});

export interface DealNameLinkProps {
  dealId: number;
  /** What the row shows, verbatim. */
  dealName: string;
  className?: string;
  title?: string;
}

export function DealNameLink({ dealId, dealName, className, title }: DealNameLinkProps): ReactElement {
  const { open, pendingId } = useContext(DealQuickEditContext);
  const busy = pendingId === dealId;

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // ⛔ Every one of these is a RETURN: an untouched event is an ordinary link.
    if (open === null) return;
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    open(dealId, dealName);
  };

  return (
    <Link
      to={`/deals/${dealId}`}
      className={className}
      {...(title !== undefined ? { title } : {})}
      {...(busy ? { 'aria-busy': true } : {})}
      onClick={handleClick}
    >
      {dealName}
      {busy && (
        <span aria-hidden="true" className="ml-1 text-fg-subtle">
          …
        </span>
      )}
    </Link>
  );
}

export interface DealQuickEditProps {
  /** Re-read the surface's own data after a save. Not optional. */
  onSaved: () => void;
  children: ReactNode;
}

/** The one mounted deal edit form behind every {@link DealNameLink} beneath it. */
export function DealQuickEdit({ onSaved, children }: DealQuickEditProps): ReactElement {
  const { ability } = useAuth();
  /*
   * ⛔ `DealFormModal`'s Save consults no ability, so the gate is here: a reader
   * who may not update deals gets the ordinary navigation to the detail page.
   */
  const canOpen = ability.canUpdate('deals');

  const [pendingId, setPendingId] = useState<number | null>(null);
  const [failure, setFailure] = useState<{ dealName: string; message: string } | null>(null);
  const [deal, setDeal] = useState<Deal | null>(null);

  const requestRef = useRef<AbortController | null>(null);
  useEffect(() => () => requestRef.current?.abort(), []);

  const open = useCallback((dealId: number, dealName: string) => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;

    setFailure(null);
    setPendingId(dealId);

    void api
      .getDeal(dealId, controller.signal)
      .then((envelope) => {
        if (controller.signal.aborted) return;
        setDeal(envelope.data);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setFailure({
          dealName,
          message: caught instanceof Error ? caught.message : 'The request did not complete.',
        });
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setPendingId(null);
      });
  }, []);

  const context = useMemo<DealQuickEditContextValue>(
    () => ({ open: canOpen ? open : null, pendingId }),
    [canOpen, open, pendingId],
  );

  return (
    <DealQuickEditContext.Provider value={context}>
      {failure !== null && (
        <Notice
          tone="danger"
          role="alert"
          title={
            <span className="[overflow-wrap:anywhere]">
              Deal {failure.dealName} could not be opened.
            </span>
          }
          action={
            <Button variant="ghost" size="sm" className="shrink-0" onClick={() => setFailure(null)}>
              Dismiss
            </Button>
          }
        >
          <span className="[overflow-wrap:anywhere]">{failure.message}</span>
        </Notice>
      )}

      {children}

      {/* Mounted only while a deal is open, so every open starts fresh. */}
      {deal !== null && (
        <DealFormModal
          open
          deal={deal}
          onClose={() => setDeal(null)}
          onSaved={() => {
            // ⚠️ `onSaved` is a CLOSE; the reload is what matters.
            setDeal(null);
            onSaved();
          }}
        />
      )}
    </DealQuickEditContext.Provider>
  );
}
